import { createFileRoute, Link } from "@tanstack/react-router";
import { ChevronLeft, Bell, MapPin, MessageSquare, Smartphone } from "lucide-react";
import { AppShell } from "@/components/app-shell";
import { DCard, HeroCard, Pill, SectionHeader } from "@/components/dryvn";
import { useNotificationPrefs, useUpdateNotifications } from "@/lib/queries";

export const Route = createFileRoute("/more/settings/notifications")({
  head: () => ({
    meta: [
      { title: "Notifications — Dryvn Life" },
      { name: "description", content: "SMS, push, digest cadence, and local suggestions." },
    ],
  }),
  component: NotificationsPage,
});

function Toggle({
  checked,
  onChange,
  label,
  desc,
  Icon,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  desc: string;
  Icon: typeof Bell;
}) {
  return (
    <DCard className="flex items-center gap-3">
      <span
        className="grid h-11 w-11 shrink-0 place-items-center rounded-full"
        style={{ backgroundColor: "color-mix(in oklab, var(--dryvn-life) 30%, transparent)" }}
      >
        <Icon className="h-5 w-5" style={{ color: "var(--dryvn-primary-deep)" }} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="font-medium" style={{ color: "var(--dryvn-ink)" }}>
          {label}
        </p>
        <p className="text-xs" style={{ color: "var(--dryvn-grey)" }}>
          {desc}
        </p>
      </div>
      <button
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className="relative h-6 w-11 shrink-0 rounded-full transition-colors"
        style={{
          backgroundColor: checked ? "var(--dryvn-primary)" : "var(--dryvn-line)",
        }}
      >
        <span
          className="absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all"
          style={{ left: checked ? "1.375rem" : "0.125rem" }}
        />
      </button>
    </DCard>
  );
}

function NotificationsPage() {
  const { data: prefs } = useNotificationPrefs();
  const update = useUpdateNotifications();

  const set = (patch: Partial<NonNullable<typeof prefs>>) => update.mutate(patch);

  return (
    <AppShell>
      <Link
        to="/more/settings"
        className="mb-4 inline-flex items-center gap-1 text-sm"
        style={{ color: "var(--dryvn-grey)" }}
      >
        <ChevronLeft className="h-4 w-4" /> Settings
      </Link>

      <HeroCard
        eyebrow="Notifications"
        title={
          <>
            When Dryvn{" "}
            <em className="italic" style={{ color: "var(--dryvn-primary-deep)" }}>
              speaks up
            </em>
            .
          </>
        }
      >
        <Pill tone="muted">Text-first · always calm</Pill>
      </HeroCard>

      <div className="mt-8 space-y-3">
        <SectionHeader eyebrow="Channels" title="How you hear from Dryvn" />
        <Toggle
          Icon={MessageSquare}
          label="SMS"
          desc="Nudges, confirmations, and morning briefings."
          checked={prefs?.smsEnabled ?? true}
          onChange={(v) => set({ smsEnabled: v })}
        />
        <Toggle
          Icon={Smartphone}
          label="Push"
          desc="Only when it's time-sensitive."
          checked={prefs?.pushEnabled ?? true}
          onChange={(v) => set({ pushEnabled: v })}
        />

        <SectionHeader eyebrow="Discovery" title="What the agent surfaces" />
        <Toggle
          Icon={MapPin}
          label="Local suggestions (up to 3/week)"
          desc="Farmers' markets, family events, and interest-based happenings nearby."
          checked={prefs?.localSuggestions ?? true}
          onChange={(v) => set({ localSuggestions: v })}
        />

        <p className="pt-2 text-[11px] leading-snug" style={{ color: "var(--dryvn-grey)" }}>
          Connect Facebook and Instagram in Integrations to improve suggestions. Dryvn never posts
          and never reads your messages.
        </p>
      </div>
    </AppShell>
  );
}
